// inline stroke icons - zero deps, keeps the legacy bundle small

interface IconProps {
  size?: number;
}

function base(size: number) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true
  };
}

// exported Figma glyphs keep their own viewBox and stroke width; only the colour becomes currentColor
function stroked(width: number, height: number, viewBox: string, strokeWidth: number) {
  return {
    width,
    height,
    viewBox,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true
  };
}

function solid(width: number, height: number, viewBox: string) {
  return { width, height, viewBox, fill: 'currentColor', 'aria-hidden': true };
}

export function SearchIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.6-3.6" />
    </svg>
  );
}

// "Trang chủ" nav glyph - house with a door
export function HomeIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M3 10.5L12 3l9 7.5" />
      <path d="M5 9v10a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V9" />
    </svg>
  );
}

export function FolderIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    </svg>
  );
}

// "Kho bài" rail glyph - archive box, lid above a tray
export function ArchiveIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="3" y="3.5" width="18" height="4.5" rx="1.5" />
      <path d="M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8" />
      <path d="M10 12h4" />
    </svg>
  );
}

// "Tải lên" rail glyph - cloud with an up arrow
export function UploadIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M20 16.58A5 5 0 0018 7h-1.26A8 8 0 104 15.25" />
      <path d="M12 12.5v8M8.7 15.8L12 12.5l3.3 3.3" />
    </svg>
  );
}

export function LinkIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M10 13a5 5 0 0 0 7.1.1l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1" />
      <path d="M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.1-1.1" />
    </svg>
  );
}

export function CheckCircleIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.2l2.4 2.4 4.6-4.9" />
    </svg>
  );
}

export function BookIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M3 5.5h6a2.5 2.5 0 012.5 2.5v11A2 2 0 009.5 17H3z" />
      <path d="M21 5.5h-6A2.5 2.5 0 0012.5 8v11A2 2 0 0114.5 17H21z" />
    </svg>
  );
}

export function FileIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}

export function BoardIcon({ size = 20 }: IconProps) {
  return (
    <svg {...stroked(size, size, '0 0 19 19', 2.35294)}>
      <path d="M1.56812 2.35303H17.2556M16.4713 2.35303V10.9805C16.4713 11.3965 16.306 11.7955 16.0118 12.0897C15.7176 12.3838 15.3186 12.5491 14.9025 12.5491H3.92124C3.50519 12.5491 3.10617 12.3838 2.81197 12.0897C2.51777 11.7955 2.35249 11.3965 2.35249 10.9805V2.35303M5.49 16.4707L9.41188 12.5491L13.3338 16.4707" />
    </svg>
  );
}

export function ClockIcon({ size = 16 }: IconProps) {
  return (
    <svg {...stroked(size, size, '0 0 14 14', 2)}>
      <path d="M7 3.49978V7.00006L9.33352 8.16682M12.8338 7.00006C12.8338 10.222 10.2219 12.8339 7 12.8339C3.77808 12.8339 1.1662 10.222 1.1662 7.00006C1.1662 3.77814 3.77808 1.16626 7 1.16626C10.2219 1.16626 12.8338 3.77814 12.8338 7.00006Z" />
    </svg>
  );
}

export function ChevronDownIcon({ size = 16 }: IconProps) {
  return (
    <svg {...stroked(size, size / 2, '0 0 10 5', 1.5)}>
      <path d="M0.750061 0.75L4.75006 3.75L8.75006 0.75" />
    </svg>
  );
}

export function ChevronRightIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M9.5 5l7 7-7 7" />
    </svg>
  );
}

export function ChevronLeftIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M14.5 5l-7 7 7 7" />
    </svg>
  );
}

// approved Figma sparkle - 20x20 viewBox, so it cannot use base()
export function SparkleIcon({ size = 22 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden={true}>
      <path d="M8.72849 12.3289C8.66052 12.0654 8.52319 11.8249 8.33079 11.6325C8.13838 11.4401 7.89794 11.3028 7.63446 11.2348L2.96373 10.0304C2.88405 10.0078 2.81391 9.95981 2.76397 9.89372C2.71403 9.82763 2.68701 9.74706 2.68701 9.66422C2.68701 9.58139 2.71403 9.50081 2.76397 9.43473C2.81391 9.36864 2.88405 9.32064 2.96373 9.29803L7.63446 8.09285C7.89784 8.02494 8.13823 7.88773 8.33062 7.69547C8.52302 7.50321 8.6604 7.26292 8.72849 6.99959L9.93291 2.32885C9.95529 2.24885 10.0032 2.17837 10.0694 2.12817C10.1356 2.07796 10.2164 2.05078 10.2995 2.05078C10.3826 2.05078 10.4634 2.07796 10.5295 2.12817C10.5957 2.17837 10.6437 2.24885 10.6661 2.32885L11.8697 6.99959C11.9377 7.26306 12.075 7.5035 12.2674 7.69591C12.4598 7.88831 12.7003 8.02564 12.9637 8.09361L17.6345 9.29727C17.7148 9.31942 17.7856 9.36731 17.8361 9.4336C17.8866 9.49989 17.9139 9.58091 17.9139 9.66422C17.9139 9.74754 17.8866 9.82856 17.8361 9.89485C17.7856 9.96113 17.7148 10.009 17.6345 10.0312L12.9637 11.2348C12.7003 11.3028 12.4598 11.4401 12.2674 11.6325C12.075 11.8249 11.9377 12.0654 11.8697 12.3289L10.6653 16.9996C10.6429 17.0796 10.595 17.1501 10.5288 17.2003C10.4626 17.2505 10.3818 17.2777 10.2987 17.2777C10.2156 17.2777 10.1349 17.2505 10.0687 17.2003C10.0025 17.1501 9.95453 17.0796 9.93214 16.9996L8.72849 12.3289Z" fill="currentColor" stroke="currentColor" strokeWidth={1.86074} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16.6665 2.5V5.83333" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.3333 4.16699H15" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3.3335 14.167V15.8337" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.16667 15H2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function FileTextIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </svg>
  );
}

export function ClipboardListIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M9 4H7a2 2 0 00-2 2v13a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2h-2" />
      <rect x="9" y="2.5" width="6" height="3.5" rx="1" />
      <path d="M9 11h6M9 15h4" />
    </svg>
  );
}

export function ClipboardCheckIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M9 4H7a2 2 0 00-2 2v13a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2h-2" />
      <rect x="9" y="2.5" width="6" height="3.5" rx="1" />
      <path d="M9 13.5l2 2 4-4.5" />
    </svg>
  );
}

export function BookOpenIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 6.5C10.6 5.2 8.6 4.5 6 4.5H3v13h3c2.6 0 4.6.7 6 2z" />
      <path d="M12 6.5c1.4-1.3 3.4-2 6-2h3v13h-3c-2.6 0-4.6.7-6 2z" />
    </svg>
  );
}

export function StarIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.2 1 5.9-5.2-2.8-5.2 2.8 1-5.9L3.5 9.7l5.9-.8z" />
    </svg>
  );
}

export function PlayCircleIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M10.3 8.8l5 3.2-5 3.2z" />
    </svg>
  );
}

export function CalendarIcon({ size = 16 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 10h17M8 3.5v3M16 3.5v3" />
    </svg>
  );
}

export function ImageIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="M4 17l4.6-4.3 3.4 3 2.6-2.3L20 17.5" />
    </svg>
  );
}

export function LayersIcon({ size = 16 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 3l8.5 4.5L12 12 3.5 7.5z" />
      <path d="M4 12l8 4.3 8-4.3M4 16.4l8 4.3 8-4.3" />
    </svg>
  );
}

// same approved glyph under the plural name TodayLectureSuggestions imports
export { SparkleIcon as SparklesIcon };

export function CloseIcon({ size = 18 }: IconProps) {
  return (
    <svg {...solid(size, size, '0 0 16 16')}>
      <path fillRule="evenodd" clipRule="evenodd" d="M3.43325 3.43324C3.58322 3.28331 3.7866 3.19908 3.99866 3.19908C4.21072 3.19908 4.41409 3.28331 4.56406 3.43324L7.99731 6.86648L11.4305 3.43324C11.5043 3.35685 11.5926 3.29593 11.6901 3.25401C11.7877 3.2121 11.8926 3.19004 11.9988 3.18912C12.105 3.18819 12.2103 3.20843 12.3086 3.24864C12.4069 3.28885 12.4962 3.34823 12.5713 3.42332C12.6464 3.49841 12.7057 3.5877 12.746 3.68599C12.7862 3.78427 12.8064 3.88958 12.8055 3.99577C12.8046 4.10195 12.7825 4.20689 12.7406 4.30446C12.6987 4.40203 12.6377 4.49028 12.5614 4.56405L9.12812 7.99729L12.5614 11.4305C12.707 11.5814 12.7876 11.7834 12.7858 11.9931C12.784 12.2028 12.6999 12.4033 12.5516 12.5516C12.4033 12.6999 12.2028 12.784 11.9931 12.7858C11.7834 12.7876 11.5814 12.707 11.4305 12.5614L7.99731 9.12811L4.56406 12.5614C4.41323 12.707 4.21122 12.7876 4.00153 12.7858C3.79185 12.784 3.59127 12.6999 3.44299 12.5516C3.29471 12.4033 3.21061 12.2028 3.20878 11.9931C3.20696 11.7834 3.28757 11.5814 3.43325 11.4305L6.86649 7.99729L3.43325 4.56405C3.28332 4.41408 3.1991 4.2107 3.1991 3.99864C3.1991 3.78658 3.28332 3.58321 3.43325 3.43324Z" />
    </svg>
  );
}

// topbar-back-arrow.svg - filled arrow, 20x16, a different glyph from ChevronLeftIcon
export function BackArrowIcon({ size = 20 }: IconProps) {
  return (
    <svg {...solid(size, (size * 4) / 5, '0 0 20 16')}>
      <path fillRule="evenodd" clipRule="evenodd" d="M0.251049 7.37151C-0.0836829 7.71865 -0.0836829 8.28138 0.251049 8.62852L7.10819 15.7396C7.44292 16.0868 7.9856 16.0868 8.32034 15.7396C8.65508 15.3925 8.65508 14.8298 8.32034 14.4826L2.92647 8.8889H19.1429C19.6162 8.8889 20 8.49092 20 8.00001C20 7.50911 19.6162 7.11112 19.1429 7.11112H2.92647L8.32034 1.51743C8.65508 1.1703 8.65508 0.607477 8.32034 0.260347C7.9856 -0.0867824 7.44292 -0.0867824 7.10819 0.260347L0.251049 7.37151Z" />
    </svg>
  );
}

// toolbar-trang.svg - the source masks an inside stroke; plain rects inset by half the width match it without mask ids
export function PageIcon({ size = 18 }: IconProps) {
  return (
    <svg {...stroked(size, size, '0 0 18 18', 3)}>
      <rect x="1.5" y="1.5" width="15" height="7" />
      <rect x="1.5" y="13.5" width="4" height="3" />
      <rect x="6.75" y="12.75" width="4.5" height="4.5" strokeWidth={1.5} />
      <rect x="12.5" y="13.5" width="4" height="3" />
    </svg>
  );
}

export function ArrowRightIcon({ size = 15 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M4 12h15M13 6l6 6-6 6" />
    </svg>
  );
}

export function PaperclipIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M20 11.5l-7.8 7.8a4.5 4.5 0 01-6.4-6.4l7.5-7.5a3 3 0 014.3 4.3l-7.6 7.6a1.5 1.5 0 01-2.2-2.1l6.9-6.9" />
    </svg>
  );
}

export function PlusIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function AtIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <circle cx="12" cy="12" r="4" />
      <path d="M16 8v5a3 3 0 006 0v-1a9 9 0 10-3.5 7.1" />
    </svg>
  );
}

export function MicIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 4a3 3 0 013 3v4a3 3 0 01-6 0V7a3 3 0 013-3z" />
      <path d="M5.5 11a6.5 6.5 0 0013 0M12 17.5V21" />
    </svg>
  );
}

export function EditSquareIcon({ size = 16 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M19 13.5V19a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h5.5" />
      <path d="M15.5 4.5l4 4L12 16H8v-4z" />
    </svg>
  );
}

// bare pencil - the row action in kho bài tập, where EditSquareIcon's frame is too heavy
export function PencilIcon({ size = 16 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M15.5 4.5l4 4L9 19H5v-4z" />
      <path d="M13.5 6.5l4 4" />
    </svg>
  );
}

export function TrashIcon({ size = 16 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M4 6.5h16" />
      <path d="M9.5 6.5V4.5h5v2" />
      <path d="M6.5 6.5l1 12a2 2 0 002 2h5a2 2 0 002-2l1-12" />
      <path d="M10.5 10.5v6M13.5 10.5v6" />
    </svg>
  );
}

// "hide sidebar" glyph - a frame with its left column filled
export function PanelCollapseIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="3" y="4.5" width="18" height="15" rx="2" />
      <path d="M9 4.5v15" />
      <path d="M4.6 7.5h2.8M4.6 10.5h2.8M4.6 13.5h2.8M4.6 16.5h2.8" />
    </svg>
  );
}

export function VideoIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="3" y="6" width="12.5" height="12" rx="2.5" />
      <path d="M15.5 10.5l5-2.6v8.2l-5-2.6z" />
    </svg>
  );
}

export function SlideIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="3" y="4.5" width="18" height="12" rx="2" />
      <path d="M12 16.5v3M9 19.5h6" />
    </svg>
  );
}

export function ClipboardIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M9 4.5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-12a2 2 0 00-2-2h-2" />
      <rect x="9" y="2.8" width="6" height="3.4" rx="1.2" />
      <path d="M8.5 11h7M8.5 14.5h4.5" />
    </svg>
  );
}

// generic "nothing here yet" mark - the EmptyState default
export function InboxIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M3 13l2.4-7.2A2 2 0 017.3 4.4h9.4a2 2 0 011.9 1.4L21 13" />
      <path d="M3 13h4.2l1.2 2.4h7.2L16.8 13H21v4.6a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    </svg>
  );
}

// load/connection failure mark
export function AlertTriangleIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M10.3 4.3 2.8 17a2 2 0 0 0 1.7 3h15a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z" />
      <path d="M12 9.5v4" />
      <path d="M12 16.8h.01" />
    </svg>
  );
}

export function ArrowLeftIcon({ size = 15 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M20 12H5M11 6l-6 6 6 6" />
    </svg>
  );
}

// "Đáp án" quiz-rail glyph - an answer sheet with the key ticked on it. Two shapes and no ruled
// interior: this is read across a classroom, where fine detail inside the sheet turns to mush.
export function AnswerKeyIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="4" y="3" width="16" height="18" rx="2.5" />
      <path d="M8.2 12.2l2.8 2.8 5-5.6" strokeWidth="2.2" />
    </svg>
  );
}

// "Kết quả" quiz-rail glyph - the report bars the results panel draws. 5.5px pitch on a 24 box
// keeps ~3.3px of white between bars at the 24px it renders at.
export function ChartBarIcon({ size = 22 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M4 20h16" />
      <path d="M6.5 20v-5.5" strokeWidth="2.2" />
      <path d="M12 20V6.5" strokeWidth="2.2" />
      <path d="M17.5 20v-9" strokeWidth="2.2" />
    </svg>
  );
}

// The stroked twin of CloseIcon, which is a filled glyph. Used where a close control sits beside a
// base() glyph and the filled one reads heavier than its neighbour; two strokes, nothing to lose.
export function CloseLineIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M6.5 6.5l11 11" />
      <path d="M17.5 6.5l-11 11" />
    </svg>
  );
}

// "Ghim vào cạnh bảng" - a board with one side column filled. PanelCollapseIcon draws four ruled
// lines at a 3px pitch, which merge into a blob below ~20px; this is two shapes with no interior
// detail, so it survives a 18px render in muted grey as well as in the pressed blue.
export function DockSideIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <path d="M17 8v8" strokeWidth="4.5" />
    </svg>
  );
}

// drag-handle glyph on every floating card - a four-way move cross. The bar it replaces was 24x2,
// invisible as an affordance and unhittable on the 65" panel; arrowheads say "this thing moves".
export function MoveIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 3v18M3 12h18" />
      <path d="M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3" />
    </svg>
  );
}

export function ExpandIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
    </svg>
  );
}

export function ShrinkIcon({ size = 20 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
    </svg>
  );
}
